import { quakeClass } from "../../../shared/sky/quakes";
import type { Quake } from "../../../shared/sky/types";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useQuakes } from "./api";
import { NeedsLocation, remPx, SkyFoot, useDistance } from "./common";

interface QuakesConfig {
  radiusKm: number;
  minMag: number;
}

function magClass(mag: number): string {
  if (mag >= 6) return "mag-6";
  if (mag >= 5) return "mag-5";
  if (mag >= 4) return "mag-4";
  if (mag >= 3) return "mag-3";
  return "mag-2";
}

function Quakes({ config, size, fullscreen, openFullscreen }: WidgetProps<QuakesConfig>) {
  const { settings } = useHousehold();
  useNow("minute");
  const dist = useDistance();
  const q = useQuakes(config.radiusKm, config.minMag);
  const state = dataStateOf(q, 40 * 60_000);
  if (!settings.location) return <NeedsLocation icon="alert" title="Earthquakes need a home location" detail="Set a general location to see quakes nearby." compact={size.variant === "compact"} />;
  const data = q.data;
  const scope = `M${config.minMag}+ within ${dist(config.radiusKm, 10)}`;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="alert" title="Earthquake data unavailable" detail={q.error?.message} />;

  const compact = size.variant === "compact";
  // Rows are ~3.3rem (3.2rem + divider); header, footer and padding take ~7rem. Never show a clipped row.
  const rem = remPx();
  const max = fullscreen ? 30 : Math.max(1, Math.floor((size.height - (compact ? 1.2 : 7) * rem) / (3.35 * rem)));
  const shown = data.quakes.slice(0, max);
  const biggest = data.quakes.reduce<Quake | null>((a, b) => (!a || b.mag > a.mag ? b : a), null);

  return (
    <div className={`sky sky-quakes ${compact ? "is-compact" : ""}`}>
      {!compact && (
        <WidgetHeader
          title="Earthquakes"
          onTitle={fullscreen ? undefined : openFullscreen}
          meta={data.quakes.length ? `${data.quakes.length} in ${data.windowDays} days${biggest ? ` · largest M${biggest.mag.toFixed(1)}` : ""}` : `Past ${data.windowDays} days`}
        />
      )}
      {shown.length === 0 ? (
        <EmptyState icon="check" title="All quiet" detail={`No ${scope} in the past ${data.windowDays} days.`} />
      ) : (
        <ul className="quake-list">
          {shown.map((e) => (
            <li key={e.id} className="quake-row">
              <span className={`quake-mag ${magClass(e.mag)}`} aria-label={`Magnitude ${e.mag.toFixed(1)}`}>
                {e.mag.toFixed(1)}
              </span>
              <span className="sky-row-main">
                <strong>{e.place}</strong>
                <span className="muted">
                  {dist(e.distanceKm)} away · {quakeClass(e.mag)}
                  {e.tsunami && " · tsunami message issued"}
                </span>
              </span>
              <span className="sky-row-side">{relativeAge(e.time)}</span>
            </li>
          ))}
        </ul>
      )}
      {!compact && <SkyFoot source={`USGS · ${scope}`} stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

export const quakesWidget: WidgetDefinition<QuakesConfig> = {
  type: "quakes",
  version: 1,
  name: "Earthquakes nearby",
  description: "Recent earthquakes near home from the USGS real-time feed: magnitude, place, distance and time.",
  icon: "alert",
  category: "weather",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 360,
  defaults: { radiusKm: 500, minMag: 2.5 },
  settings: [
    {
      key: "radiusKm",
      label: "Distance from home",
      type: "select",
      options: [
        { value: 100, label: "100 km (60 mi)" },
        { value: 250, label: "250 km (155 mi)" },
        { value: 500, label: "500 km (310 mi)" },
        { value: 1000, label: "1,000 km (620 mi)" },
        { value: 2000, label: "2,000 km (1,240 mi)" },
      ],
    },
    {
      key: "minMag",
      label: "Smallest magnitude",
      type: "select",
      options: [
        { value: 2.5, label: "2.5 — sometimes felt" },
        { value: 3.5, label: "3.5 — often felt" },
        { value: 4.5, label: "4.5 — felt widely" },
      ],
    },
  ],
  provider: "usgs-quakes",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Quakes,
};
